
import { useState } from 'react';
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface CreateProfileData {
  username: string;
  firstName?: string;
  lastName?: string;
  birthdate?: string;
  relationshipType?: 'managed' | 'gift' | 'memorial';
}

export const useAdditionalProfiles = () => {
  const { 
    additionalProfiles, 
    isLoadingProfiles, 
    createAdditionalProfile, 
    refreshProfiles 
  } = useProfileSwitch();
  const { toast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleCreateProfile = async (profileData: CreateProfileData) => {
    setIsCreating(true);
    
    try {
      const profileId = await createAdditionalProfile({
        username: profileData.username.toLowerCase(),
        first_name: profileData.firstName,
        last_name: profileData.lastName,
        birthdate: profileData.birthdate,
        relationship_type: profileData.relationshipType
      });

      toast({
        title: 'Profile created successfully',
        description: `${profileData.firstName || profileData.username}'s timeline is ready!`
      });

      return profileId;
    } catch (error: any) {
      console.error('Error creating profile:', error);
      toast({
        title: 'Error creating profile',
        description: error.message || 'Failed to create profile. Please try again.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setIsCreating(false);
    }
  };

  const getDisplayName = (profile: any) => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name} ${profile.last_name}`;
    }
    return profile.username;
  };

  const getRelationshipLabel = (relationshipType: string) => {
    switch (relationshipType) {
      case 'gift':
        return 'Gift Timeline';
      case 'memorial':
        return 'Memorial Timeline';
      case 'managed':
      default:
        return 'Managed Timeline';
    }
  };

  const handleUpdateProfile = async (profileId: string, profileData: CreateProfileData) => {
    setIsUpdating(true);
    
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          username: profileData.username?.toLowerCase(),
          first_name: profileData.firstName,
          last_name: profileData.lastName,
          birthdate: profileData.birthdate || null,
        })
        .eq('id', profileId);

      if (error) throw error;

      // Update relationship type if changed
      if (profileData.relationshipType) {
        const { error: ownershipError } = await supabase
          .from('profile_ownerships')
          .update({
            relationship_type: profileData.relationshipType
          })
          .eq('owned_profile_id', profileId);

        if (ownershipError) throw ownershipError;
      }

      toast({
        title: 'Profile updated successfully',
        description: `${profileData.firstName || profileData.username}'s profile has been updated.`
      });

      await refreshProfiles();
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error updating profile',
        description: error.message || 'Failed to update profile. Please try again.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    setIsDeleting(true);
    
    try {
      // Call the database function to safely delete the profile
      const { data, error } = await supabase.rpc('delete_owned_profile', {
        profile_id_param: profileId,
        owner_user_id: (await supabase.auth.getUser()).data.user?.id
      });

      if (error) throw error;

      const result = data as { success: boolean; error?: string; deleted_profile?: { id: string; username: string; first_name?: string; last_name?: string } };

      if (!result?.success) {
        throw new Error(result?.error || 'Failed to delete profile');
      }

      const deletedProfile = result.deleted_profile;
      const displayName = deletedProfile?.first_name 
        ? `${deletedProfile.first_name} ${deletedProfile.last_name || ''}`.trim()
        : deletedProfile?.username;

      toast({
        title: 'Profile deleted successfully',
        description: `${displayName}'s profile and all associated data have been permanently deleted.`
      });

      await refreshProfiles();
    } catch (error: any) {
      console.error('Error deleting profile:', error);
      toast({
        title: 'Error deleting profile',
        description: error.message || 'Failed to delete profile. Please try again.',
        variant: 'destructive'
      });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  return {
    additionalProfiles,
    isLoadingProfiles,
    isCreating,
    isUpdating,
    isDeleting,
    createProfile: handleCreateProfile,
    updateProfile: handleUpdateProfile,
    deleteProfile: handleDeleteProfile,
    refreshProfiles,
    getDisplayName,
    getRelationshipLabel
  };
};
